import { Card, Col, Row, Space, Typography } from "antd";
import { useSelector } from "react-redux";
import BillingApproval from "redux/models/billingApproval";
import CommissionPlan from "redux/models/commissionPlan";
import { selectUser, selectUserId } from "redux/slices/userSlice";
import K from "utilities/constants";
import { isPermissionPresent } from "utilities/generalUtility";
import "../../../submitToBilling/submitToBilling.scss";
import { isEmpty, keyBy } from "lodash";
import { useMemo, useState } from "react";
import { Collapse } from "common/components/collapse/collapse";
import { ClientSideDataGrid } from "common/components/clientSideDataGrid/clientSideDataGrid";
import { ApprovalTotal } from "../../approvalTotal";
import { getApprovalsTotal, getThresholdTypeDisplayValue } from "../../utils";
import { Columns } from "./columns";
import { CreateStatementButton } from "./createStatementButton/createStatementButton";
import { CommissionApprovalDetailsModal } from "./commissionDetailsModal";

const { Title } = Typography;

const SelectedRowsInitialState = {
  total: 0,
  exceptionsTotal: {},
  data: [],
};

export const RecruiterCommissionApprovals = ({
  recruiterId,
  recruiterName,
}) => {
  const userId = useSelector(selectUserId);
  const userSlice = useSelector(selectUser);

  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(SelectedRowsInitialState);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRecordHistory, setSelectedRecordHistory] = useState([]);

  const [approvals, setApprovals] = useState([]);

  const [recruiterDetails, setRecruiterDetails] = useState({
    thresholdType: null,
    defaultBillingThreshold: null,
    clientExceptions: [],
  });

  const { clientExceptions } = recruiterDetails;

  const exceptionByClientId = useMemo(
    () => keyBy(clientExceptions, "clientId"),
    [clientExceptions],
  );

  const onRowClicked = async (event) => {
    const history = await getBillHistory({
      type: K.Invoice.Type.Commission,
      billingId: event.data.id,
    });

    setSelectedRecord(event.data);
    setSelectedRecordHistory(history);
  };

  const getBillHistory = async (payload) => {
    try {
      return await BillingApproval.getSubmittedBillHistory(payload);
    } catch (err) {
      console.error(err);
    }
  };

  const onSelectionChanged = (e) => {
    const selectedRows = e.api.getSelectedRows();

    const { total, selected, exceptionsTotal } = getApprovalsTotal(
      selectedRows,
      recruiterDetails.thresholdType,
      exceptionByClientId,
    );

    setSelectedRows((prev) => ({
      ...prev,
      total,
      exceptionsTotal,
      data: selected,
    }));
  };

  const getCommissionApprovals = async (recruiterID) => {
    setLoading(true);

    try {
      const res = await CommissionPlan.getCommissionApproval(recruiterID, true);
      const data = res[0];

      setRecruiterDetails({
        clientExceptions: data.clientExceptions || [],
        defaultBillingThreshold: data.defaultBillingThreshold,
        thresholdType: data.thresholdType,
      });
      setApprovals(data.recruiterBasic.approvals);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataFromServer = (recruiterID) => {
    if (recruiterID.length > 0) {
      getCommissionApprovals(recruiterID);
    }
  };

  const onStatementCreated = () => {
    setApprovals((prev) =>
      prev.filter((a) => !selectedRows.data.includes(a.id)),
    );

    setSelectedRows(SelectedRowsInitialState);
  };

  const onAmountEdited = async (amount) => {
    setApprovals((prev) =>
      prev.map((a) =>
        a.id === selectedRecord.id ? { ...a, duplicateAmount: amount } : a,
      ),
    );

    setSelectedRecord((prev) => ({ ...prev, duplicateAmount: amount }));

    const history = await getBillHistory({
      type: K.Invoice.Type.Commission,
      billingId: selectedRecord.id,
    });

    setSelectedRecordHistory(history);
  };

  return (
    <>
      <Collapse onChange={fetchDataFromServer}>
        <Collapse.Panel header={recruiterName} key={recruiterId}>
          {loading ? (
            <Card loading />
          ) : (
            <Card
              bodyStyle={{ padding: 0 }}
              headStyle={{ padding: 0 }}
              bordered={false}
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <Row gutter={[12, 12]}>
                  <Col span="50%">
                    <div
                      style={{
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Title
                        level={5}
                        style={{ margin: 0 }}
                      >{`Default ${getThresholdTypeDisplayValue(
                        recruiterDetails.thresholdType,
                        recruiterDetails.defaultBillingThreshold,
                      )}`}</Title>
                    </div>
                    {clientExceptions.map((a) => (
                      <div key={a.id}>
                        <Typography.Text>{`${
                          a.client.name
                        } ${getThresholdTypeDisplayValue(
                          a.thresholdType,
                          a.defaultBillingThreshold,
                        )}`}</Typography.Text>
                      </div>
                    ))}
                  </Col>
                  <Col flex="auto">
                    <ApprovalTotal
                      thresholdType={recruiterDetails.thresholdType}
                      defaultBillingThreshold={
                        recruiterDetails?.defaultBillingThreshold
                      }
                      exceptions={recruiterDetails.clientExceptions || []}
                      total={selectedRows?.total}
                      exceptionsTotal={selectedRows?.exceptionsTotal}
                    />
                  </Col>
                  <Col>
                    {" "}
                    {isPermissionPresent(
                      K.Permissions.CreateCommissionStatement,
                      userSlice.roles,
                    ) && (
                      <CreateStatementButton
                        recruiterId={recruiterId}
                        approvals={selectedRows.data}
                        threshold={{
                          type: recruiterDetails.thresholdType,
                          defaultValue:
                            recruiterDetails.defaultBillingThreshold,
                        }}
                        clientExceptions={clientExceptions}
                        selectedRows={selectedRows}
                        disabled={isEmpty(selectedRows.data)}
                        onSuccess={onStatementCreated}
                      />
                    )}
                  </Col>
                </Row>
                <div
                  className="ag-theme-alpine s2-theme-style"
                  style={{
                    height: 675,
                  }}
                >
                  <ClientSideDataGrid
                    height={675}
                    userId={userId}
                    configKey={
                      K.AgGridTable.Keys.CommissionApprovalsVisibleColumn
                    }
                    defaultColumnConfig={Columns}
                    rowData={approvals}
                    rowSelection="multiple"
                    suppressRowClickSelection
                    onRowClicked={onRowClicked}
                    onSelectionChanged={onSelectionChanged}
                    rowStyle={{ cursor: "pointer" }}
                  />
                </div>
              </Space>
            </Card>
          )}
        </Collapse.Panel>
      </Collapse>

      {selectedRecord && (
        <CommissionApprovalDetailsModal
          data={selectedRecord}
          history={selectedRecordHistory}
          onCancel={() => setSelectedRecord(null)}
          onAmountEdited={onAmountEdited}
        />
      )}
    </>
  );
};
